import { UploadFile } from 'antd/lib/upload/interface';
import { AxiosRequestConfig } from 'axios';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryResult,
} from 'react-query';
import { FileAttachment } from 'api/attachments';
import { ProjectId } from 'api/models';
import { ProjectFilesAPI } from 'api/project-files';
import {
  ProjectFileGroup,
  ProjectTaskAttachments,
} from 'api/project-files/types';
import { CreateGroupFormProps } from 'components/organisms/projects/files/create-group-modal';
import { UpdateFolderFormProps } from 'components/organisms/projects/files/update-folder-modal';
import { GENERAL_API_STALE } from 'config/query-config';

type ProjectFileGroupsQueryKey = string;
type ProjectFileDriveQueryKey = string;
type ProjectTaskAttachmentsQueryKey = string;

export const getProjectFileGroupsQueryKey = ([
  ,
  projectId,
]: ProjectId): ProjectFileGroupsQueryKey => `PROJECT_${projectId}_FILE_GROUPS`;

export const useProjectFileGroupsQuery = (
  projectId: ProjectId,
): UseQueryResult<ProjectFileGroup[]> =>
  useQuery(
    getProjectFileGroupsQueryKey(projectId),
    () => ProjectFilesAPI.getFileGroups(projectId),
    {
      staleTime: GENERAL_API_STALE,
    },
  );

export const getProjectFileDriveQueryKey = ([
  ,
  projectId,
]: ProjectId): ProjectFileDriveQueryKey => `PROJECT_${projectId}_FILE_DRIVE`;

export const useProjectFileDriveQuery = (
  projectId: ProjectId,
): UseQueryResult<{
  folders: ProjectFileGroup[];
  tasks: ProjectTaskAttachments[];
}> =>
  useQuery(
    getProjectFileDriveQueryKey(projectId),
    () => ProjectFilesAPI.getFileDrive(projectId),
    {
      staleTime: GENERAL_API_STALE,
    },
  );

export const getProjectTaskAttachmentsQueryKey = ([
  ,
  projectId,
]: ProjectId): ProjectTaskAttachmentsQueryKey =>
  `PROJECT_${projectId}_TASK_ATTACHMENTS`;

export const useProjectTaskAttachmentsQuery = (
  projectId: ProjectId,
): UseQueryResult<ProjectTaskAttachments[]> =>
  useQuery(
    getProjectTaskAttachmentsQueryKey(projectId),
    () => ProjectFilesAPI.getTaskAttachments(projectId),
    {
      staleTime: GENERAL_API_STALE,
    },
  );

const createFileGroupMutationFn = (
  projectId: ProjectId,
  { groupName, permission }: CreateGroupFormProps,
) =>
  ProjectFilesAPI.createFileGroup(projectId, {
    folderName: groupName,
    isPrivate: permission === 'private',
  });

export const useCreateProjectFileGroupMutation = (
  projectId: ProjectId,
  options: UseMutationOptions<
    ProjectFileGroup,
    unknown,
    CreateGroupFormProps
  > = {},
) =>
  useMutation(
    (formValues) => createFileGroupMutationFn(projectId, formValues),
    options,
  );

const updateFolderMutationFn = (
  projectId: ProjectId,
  folderId: number,
  { folderName }: UpdateFolderFormProps,
) =>
  ProjectFilesAPI.updateFolder(projectId, folderId, {
    folderName,
  });

export const useUpdateProjectFolderMutation = (
  projectId: ProjectId,
  options: UseMutationOptions<
    ProjectFileGroup,
    unknown,
    { formValues: UpdateFolderFormProps; folderId: number }
  > = {},
) =>
  useMutation(
    ({ formValues, folderId }) =>
      updateFolderMutationFn(projectId, folderId, formValues),
    options,
  );

const uploadFileMutationFn = (
  file: UploadFile,
  data: { projectId: number; folderId: number },
  reqConfig: AxiosRequestConfig,
) => ProjectFilesAPI.uploadFile(file, data, reqConfig);

export const useUploadProjectFileMutation = (
  options: UseMutationOptions<
    FileAttachment,
    unknown,
    {
      file: UploadFile;
      data: { projectId: number; folderId: number };
      reqConfig: AxiosRequestConfig;
    }
  > = {},
) => {
  return useMutation(
    ({ file, data, reqConfig }) => uploadFileMutationFn(file, data, reqConfig),
    options,
  );
};

const deleteFileMutationFn = (
  projectId: ProjectId,
  groupId: number,
  fileId: number,
) => ProjectFilesAPI.deleteFile(projectId, groupId, fileId);

export const useDeleteProjectFileMutation = (
  projectId: ProjectId,
  groupId: number,
  fileId: number,
  options: UseMutationOptions<FileAttachment, unknown> = {},
) => {
  return useMutation(
    () => deleteFileMutationFn(projectId, groupId, fileId),
    options,
  );
};

const deleteFileGroupMutationFn = (projectId: ProjectId, groupId: number) =>
  ProjectFilesAPI.deleteFileGroup(projectId, groupId);

export const useDeleteProjectFileGroupMutation = (
  projectId: ProjectId,
  groupId: number,
  options: UseMutationOptions<ProjectFileGroup, unknown> = {},
) => {
  return useMutation(
    () => deleteFileGroupMutationFn(projectId, groupId),
    options,
  );
};

const linkFilesToProjectsMutationFn = (
  projectId: ProjectId,
  groupId: number,
  fileIds: number[],
  reqConfig?: AxiosRequestConfig,
) =>
  ProjectFilesAPI.linkFilesToProjects(projectId, groupId, fileIds, reqConfig);

export const useLinkFilesToProjectsMutation = (
  projectId: ProjectId,
  options: UseMutationOptions<
    UploadFile,
    unknown,
    { fileIds: number[]; groupId: number; reqConfig?: AxiosRequestConfig }
  > = {},
) => {
  return useMutation(
    ({ groupId, fileIds, reqConfig }) =>
      linkFilesToProjectsMutationFn(projectId, groupId, fileIds, reqConfig),
    options,
  );
};
